import { useState, useCallback } from "react";
import { Keyboard } from "react-native";
import { ValidationError, RateLimitError, NetworkError } from "@src/services/api/legacy-api-request";
import { legacyApi } from "@src/services/api/legacy-client";
import { useAmplitude } from "@src/hooks/useAmplitude";
import { ANALYTICS_EVENTS } from "@src/services/analytics/events";
import { showSuccessToast } from "@src/utils/toast";
import { router } from "expo-router";

const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email.trim());
};

export interface UseResetPasswordReturn {
  email: string;
  setEmail: (email: string) => void;
  isLoading: boolean;
  error: string | null;
  handleResetPassword: () => Promise<void>;
  handleSubmitEditing: () => void;
  isEmailValid: boolean;
}

export function useResetPassword(): UseResetPasswordReturn {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { track } = useAmplitude();

  const isEmailValid = isValidEmail(email);

  const handleResetPassword = useCallback(async () => {
    // Clear previous error
    setError(null);

    // Validate email format
    const trimmedEmail = email.trim();

    if (!trimmedEmail) {
      setError("Please enter your email address.");
      return;
    }

    if (!isValidEmail(trimmedEmail)) {
      setError("Please enter a valid email address.");
      return;
    }

    setIsLoading(true);
    Keyboard.dismiss();

    try {
      await legacyApi.resetPassword.send(trimmedEmail);
      track(ANALYTICS_EVENTS.FORGOT_PASSWORD_SENT);
      showSuccessToast({
        message: "Email Sent",
        description: `A password reset link has been sent to ${trimmedEmail}.`,
      });
      router.back();
    } catch (err) {
      track(ANALYTICS_EVENTS.FORGOT_PASSWORD_FAILED, {
        error_type:
          err instanceof ValidationError ? "validation"
          : err instanceof RateLimitError ? "rate_limit"
          : err instanceof NetworkError ? "network"
          : "unknown",
      });

      if (err instanceof ValidationError) {
        setError(err.message);
        console.error(err);
      } else if (err instanceof RateLimitError) {
        setError(err.message);
        console.error(err);
      } else if (err instanceof NetworkError) {
        setError("Unable to reset password, please check your network connection and try again.");
        console.error(err);
      } else {
        setError("Unable to reset password.");
        console.error(err);
      }
    } finally {
      setIsLoading(false);
    }
  }, [email, track]);

  const handleSubmitEditing = useCallback(() => {
    if (isEmailValid) {
      handleResetPassword();
    }
  }, [isEmailValid, handleResetPassword]);

  return {
    email,
    setEmail,
    isLoading,
    error,
    handleResetPassword,
    handleSubmitEditing,
    isEmailValid,
  };
}
